
import { Flex, FormCheckbox, Input, ProviderConsumer, Text, ThemePrepared } from "@fluentui/react-northstar";
import { themeNames } from "@fluentui/react-teams";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TicketDatePicker } from "../shared/common/Controls/TicketDateTimePicker";
import { Item } from "../shared/components/NestedDropdown/NestedDropdown";
import { TypeaheadPicker } from "../shared/components/NestedDropdown/TypeaheadPicker";
import { FieldErrorText, RequiredAsterisk } from "../shared/components/FieldErrorText";
import { LabelElement } from '../shared/components/TicketForm';
import { getCardDateInputISOFormatted, getCardTimeInputISOFormatted, getISODateOnlyPartString } from "../shared/components/TicketTemplateAnswer";
import { ICustomProperty, ICustomPropertyListItem } from "../shared/interfaces/customproperties.interface";
import {  CustomProperty_TimeInputWrapperClass } from "../shared/utils/constants";
import { formatTimeInput } from "../templates/CustomComponents/Utils/CustomElementUtils";
import { CustomPropertyDataType_LABELS } from "./utils/constants";
import { CustomTimePicker } from "./CustomTimePicker";

interface Props {
reRender?: boolean;
  isTicketClosed: boolean;
  ticketId: number;
  dispatch: React.Dispatch<any>;
  customPropertiesData: string;
  showErrorOnChange?: boolean;
  customPropertiesList: ICustomProperty[];
}

export const customPropertyListFormat = (listItems: ICustomPropertyListItem[], selectedListItems: ICustomPropertyListItem[] | null) => {
    let itemsFormatedList = [];
    listItems.forEach((t: ICustomPropertyListItem) => {
        let listItemObj = {};
        listItemObj['header'] = t.Value;
        listItemObj['accessibilityitemprops'] = t.Id;
        if (selectedListItems?.some(item => item?.Id == t.Id)) {
        listItemObj['selected'] = true;
        } else {
        listItemObj['selected'] = false;
        }
        itemsFormatedList.push(listItemObj);
    });
    return itemsFormatedList;
};

export const TicketCustomProperties = ({
  reRender,
  isTicketClosed,
  ticketId,
  dispatch,
  customPropertiesData,
  showErrorOnChange,
  customPropertiesList
}: Props) => {
    const { t } = useTranslation();
    const [propertyList] = useState<ICustomProperty[]>(
        (customPropertiesList ?? [])
            .filter(p => !p.IsHidden)
            .sort((a, b) => a.Order - b.Order)
    );
    const [propertyAnswerJson, setPropertyAnswerJson] = useState<any>({});
    const [errorFields, setErrorFields] = useState<string[]>([]);
    const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);

    useEffect(() => {
        let propAnswerJson = (customPropertiesData?.length ?? 0) > 0 ? JSON.parse(customPropertiesData) : {};
        // On Create: For boolean property with no default value, set it to false to avoid the issue of having no value selected
        if (ticketId === 0) {
            propertyList.forEach(property => {
                if (CustomPropertyDataType_LABELS[property.DataType] === CustomPropertyDataType_LABELS.BOOL && !(property.PropertyId in propAnswerJson)) {
                    propAnswerJson[property.PropertyId] = false;
                }
            });
        }
        setPropertyAnswerJson(propAnswerJson);
        setIsDataLoaded(true);
    }, [customPropertiesData]);

    const updatePropertyAnswerJson = (prop: ICustomProperty, answer: any) => {
        const propKey = prop.PropertyId;
        setPropertyAnswerJson(prev => {
            const newJson = {...prev, [propKey]: answer};
            const propertyAnswerJsonStr = JSON.stringify(newJson);
            if(ticketId == 0)
                dispatch({ action: "CustomPropertiesAnswerJson", isEntity: true, data: propertyAnswerJsonStr });
            else{
                dispatch({ type: 'set-properties-answer', data: propertyAnswerJsonStr });
                dispatch({ type: 'update-ticket', data: {CustomPropertiesAnswerJson: propertyAnswerJsonStr }});
            }
            return newJson;
        });
        if(prop.IsRequired && showErrorOnChange)
            setErrorFields(prev => {
                if(!answer)
                    return [...prev, propKey];
                else
                    return prev.filter(e => e != propKey);
            });
    }

    const renderCustomProperty = (property: ICustomProperty, globalTheme: ThemePrepared<any>) => {

        let el = <></>;
        switch(CustomPropertyDataType_LABELS[property.DataType]){
            case CustomPropertyDataType_LABELS.STRING:
                el = <LabelElement label={property.Name} required={property.IsRequired} themeAsterisk>
                    <Input
                    fluid
                    required={property.IsRequired}
                    value={propertyAnswerJson[property.PropertyId] ?? ""}
                    onChange={(_, p) => {
                        updatePropertyAnswerJson(property, p.value);
                    }}
                    showSuccessIndicator={false}
                    disabled={isTicketClosed}
                    />
                </LabelElement>
                break;
            case CustomPropertyDataType_LABELS.INT:
                el = <LabelElement label={property.Name} required={property.IsRequired} themeAsterisk>
                    <Input
                    type="number"
                    fluid
                    required={property.IsRequired}
                    min={0}
                    value={propertyAnswerJson[property.PropertyId] ?? ""}
                    onChange={(_, p) => {
                        updatePropertyAnswerJson(property, p.value);
                    }}
                    showSuccessIndicator={false}
                    disabled={isTicketClosed}
                    />
                </LabelElement>
                break;
            case CustomPropertyDataType_LABELS.TIME:
                el = <LabelElement label={property.Name} required={property.IsRequired} themeAsterisk>
                    <CustomTimePicker
                        required={property.IsRequired}
                        value={formatTimeInput(propertyAnswerJson[property.PropertyId])}
                        onChange={p => {
                            const timeInput = (p.target as HTMLInputElement).value;
                            if(timeInput)
                                updatePropertyAnswerJson(property, getCardTimeInputISOFormatted(timeInput));
                            else
                                updatePropertyAnswerJson(property, timeInput);
                        }}
                        disabled={isTicketClosed}
                     />
                </LabelElement>
                break;
            case CustomPropertyDataType_LABELS.DATE:
                el = <LabelElement label={property.Name} required={property.IsRequired} themeAsterisk>
                    {TicketDatePicker(isTicketClosed,
                    propertyAnswerJson[property.PropertyId] ? new Date(propertyAnswerJson[property.PropertyId]): null,
                    null,
                    (_, p) => {
                        let selectedDate = p.value ?? null;
                        updatePropertyAnswerJson(property, getCardDateInputISOFormatted(getISODateOnlyPartString(selectedDate)));
                    },
                    undefined,
                    // Clear on every date, as on every list: emptying a required one shows its error message and keeps Save off
                    // until it is filled (saved as '' - the list's cell saves an emptied date the same way).
                    isTicketClosed ? undefined : () => updatePropertyAnswerJson(property, ''))}
                    
                </LabelElement>
                break;
            case CustomPropertyDataType_LABELS.BOOL:
                el = <FormCheckbox
                    label={<Flex>
                            <Text content={property.Name} style={{ display: 'block' }} />
                            {property.IsRequired && (
                              <RequiredAsterisk />
                            )}
                          </Flex>}
                    onChange={(_, p) => {
                        updatePropertyAnswerJson(property, p.checked);
                    }}
                    checked={(propertyAnswerJson[property.PropertyId]?.toString().toLowerCase()) === "true"}
                    disabled={isTicketClosed}
                />
                break;
            case CustomPropertyDataType_LABELS.LIST:
                let propValuesId = propertyAnswerJson[property.PropertyId];
                const asItem = (item: ICustomPropertyListItem) => ({
                    id: item.Id,
                    parentId: item.ParentId,
                    position: item.Position,
                    value: item.Value,
                    ...item
                } as Item);
                const propertyListItems = property.CustomPropertyListItems
                    .filter(pItem => !pItem.IsArchived && !pItem.IsDeleted)
                    .map(asItem);
                const defaultItem = property.CustomPropertyListItems
                    .find(pItem => !pItem.IsArchived && !pItem.IsDeleted && pItem.IsDefault);

                let isDefaultValueSet = false;
                if(ticketId == 0 && (propValuesId?.toString() ?? "").length == 0 && defaultItem && !property.IsRequired){
                    propValuesId = defaultItem.Id.toString();
                    isDefaultValueSet = true;
                }
                if(property.CustomPropertyListConfiguration?.AllowMultiple)
                {
                    el = <TypeaheadPicker
                        tabIndex={0}
                        label={property.Name}
                        items={propertyListItems}
                        heldItems={property.CustomPropertyListItems.filter(pItem => pItem.IsArchived || pItem.IsDeleted).map(asItem)}
                        defaultValue={propValuesId}
                        onChange={_ => {
                        }}
                        isMulti
                        onMultiChange={value => {
                            if(propValuesId || value.length > 0)
                                updatePropertyAnswerJson(property, value?.join(","));
                        }}
                        isRequired={property.IsRequired}
                        triggerDefaultValueSet={isDefaultValueSet}
                        disabled={isTicketClosed}
                    />
                }else{
                    const propValuesIds: string[] = propValuesId?.split(",");
                    const propValues: ICustomPropertyListItem[] = property.CustomPropertyListItems
                        .filter(p => propValuesIds?.some(v => v == p.Id.toString()));
                    const propValue = propValues[0];
                    el = <TypeaheadPicker
                        tabIndex={0}
                        label={property.Name}
                        items={propertyListItems}
                        dataItem={{
                            id: propValue?.Id as number,
                            parentId: propValue?.ParentId as number,
                            position: propValue?.Position,
                            value: propValue?.Value,
                            ...propValue
                        }}
                        defaultValue={propValue?.Value}
                        onChange={value => {
                            updatePropertyAnswerJson(property, value?.id.toString());
                        }}
                        // Always an X, as Category and Priority have: emptying a required one shows its error message and keeps
                        // Save off until it is filled (checkCustomPropsRequiredError) - an optional one may stay empty.
                        showClearBtn={!isTicketClosed}
                        isRequired={property.IsRequired}
                        triggerDefaultValueSet={isDefaultValueSet}
                        disabled={isTicketClosed}
                    />
                }
                break;
            default:
                break;
        }
        return el;
    }

    return (<ProviderConsumer render={(globalTheme) => (
        <>
            {isDataLoaded && propertyList
            .map(property => {
                const isDarkMode = globalTheme.siteVariables.theme === themeNames.Dark;
                return <Flex key={property.PropertyId} column className={CustomProperty_TimeInputWrapperClass(isDarkMode)}>
                    <div key={`customProperties-${reRender}`}>
                        {renderCustomProperty(property, globalTheme)}
                        {errorFields.indexOf(property.PropertyId) != -1 && <FieldErrorText>{(property.ErrorMessage?.length ?? 0) > 0 ? property.ErrorMessage  : t('ticket-details.edit-ticket-view.required-field-must-be-filled-out')}</FieldErrorText>}
                    </div>
                </Flex>
            })}
        </>)} 
    />)
}